import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, forkJoin, map, switchMap, take, tap } from 'rxjs';
import { appActions } from '@app/store/app.actions';
import { authActions } from '@app/store/auth/actions/auth.actions';
import { profileActions } from '@app/store/profile/actions/profile.actions';
import { sessionActions } from '@app/store/session/actions/session.actions';
import { selectSession } from '@app/store/session/selectors/session.selector';
import { deviceActions } from '../actions/device.actions';
import { selectLocked } from '../selectors/device.selector';

// Bridge: runs the lock re-auth saga across device ↔ auth ↔ session ↔ profile.
// Dependencies flow device → others, never back. No facades — coordinate through the store only.
@Injectable()
export class DeviceReauthBridge {
    private readonly actions = inject(Actions);
    private readonly store = inject(Store);
    private readonly router = inject(Router);

    // A re-auth while locked resolves the gate. The session isn't touched while locked, so it still
    // holds the pre-lock user — compare the just-authed id against it: same → unlock, different → switch.
    resolveReauth$ = createEffect(() => {
        return this.actions.pipe(
            ofType(authActions.loginWithPinSuccess),
            concatLatestFrom(() => [this.store.select(selectLocked), this.store.select(selectSession)]),
            filter(([, locked]) => locked),
            map(([{ payload: userId }, , user]) => (user?.id === userId ? deviceActions.unlock() : deviceActions.switchUser())),
        );
    });

    // Different user = logout of the previous + clean login of the new: wipe all feature state,
    // load the new user, and force a fresh workstation selection.
    switchUser$ = createEffect(() => {
        return this.actions.pipe(
            ofType(deviceActions.switchUser),
            switchMap(() => [appActions.reset(), sessionActions.getSession(), profileActions.getProfile()]),
        );
    });

    // Hold the lock overlay up through the switch until the new user's session + profile resolve,
    // so the screen never flashes the previous user or an empty header before /workstation shows.
    unlockWhenReady$ = createEffect(() => {
        return this.actions.pipe(
            ofType(deviceActions.switchUser),
            switchMap(() =>
                forkJoin([
                    this.actions.pipe(ofType(sessionActions.getSessionSuccess, sessionActions.getSessionError), take(1)),
                    this.actions.pipe(ofType(profileActions.getProfileSuccess, profileActions.getProfileError), take(1)),
                ]).pipe(map(() => deviceActions.unlock())),
            ),
        );
    });

    navigateOnSwitchUser$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(deviceActions.switchUser),
                tap(() => {
                    this.router.navigate(['/', 'workstation']);
                }),
            );
        },
        { dispatch: false },
    );
}
